/**
 * Opens the realtime connection for the signed-in session — §5-F15.
 *
 * Reconnects whenever the access token changes, which is what a silent refresh
 * produces: the socket carries the token in its handshake, so a rotated token
 * needs a fresh handshake rather than a patched header.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getAccessToken } from '@/lib/api';
import { connectRealtime, disconnectRealtime, subscribeToRoom } from '@/lib/realtime';
import { useAuth } from '@/features/auth';

export function useRealtime(): void {
  const { status, user } = useAuth();
  const qc = useQueryClient();

  useEffect(() => {
    if (status !== 'authenticated') {
      disconnectRealtime();
      return;
    }
    const token = getAccessToken();
    if (!token) return;

    const dispose = connectRealtime(token, qc);
    return dispose;
  }, [status, user?._id, qc]);
}

/**
 * Subscribes to one realtime room for as long as the component is mounted.
 *
 * Used by the task detail page and the board: task changes are emitted to the
 * task and project rooms rather than workspace-wide, so a viewer has to ask.
 */
export function useRoom(room: string | undefined): void {
  useEffect(() => {
    if (!room) return;
    return subscribeToRoom(room);
  }, [room]);
}
